import { useContext } from 'react'
import { Icon } from '@chakra-ui/icons'
import { AspectRatio, Text } from '@chakra-ui/layout'
import {
  Box,
  Tooltip,
  VStack,
  Flex,
  Image,
  Portal,
} from '@chakra-ui/react'
import type { ReactElement } from 'react'
import React from 'react'
import { MdCollections, } from 'react-icons/md'
import { MdEdit } from 'react-icons/md'
import { Link, useHistory } from 'react-router-dom'
import type { MediaLibrarySearchCollectionResponse } from '../../../../interfaces'
import {
  MediaLibraryActions,
  MediaLibraryActionType,
} from '../../../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../../../providers/PublicLinksInfoProvider'
import { ContextMenuTrigger } from '../../ContextMenu'
import { useGetQueryParams } from '../../../../hooks/useGetQueryParams'
import { colors } from '../../../../../ui-kit'
import ChangeThumbnailModal from '../../MediaOperations/ChangeThumbnailModal'
import { useSingleClickHandling } from '../../../../hooks/useSingleClickHandling'
import { useDisclosure } from '@chakra-ui/react'

interface MediaCollectionProps {
  focusedIndex: { index: number; type: string; id: string } | null
  setSelectedAssetMap: { [key: string]: Function }
  selectedAssetMap: { [key: string]: Set<any> }
  setFocusedIndex: Function
  index: number
  getIdMap: { [key: string]: Function }
  dispatch: React.Dispatch<MediaLibraryActionType | MediaLibraryActionType[]>
  mediaCollection: MediaLibrarySearchCollectionResponse
  gridTypeForDataset?: 'recent' | 'mediaCollection'
  gridOrder: string[]
  assetCountMap: { [key: string]: number }
}

const NewCollectionElement = ({
  focusedIndex,
  setSelectedAssetMap,
  selectedAssetMap,
  setFocusedIndex,
  index,
  getIdMap,
  dispatch,
  mediaCollection,
  gridTypeForDataset,
  gridOrder,
  assetCountMap,
}: MediaCollectionProps): ReactElement => {
  const {
    name,
    mediaCollectionId,
    size = 0,
    collectionThumbnail,
  } = mediaCollection

    const {
      isPublicLink,
    } = useContext(PublicLinksInfoContext)
  const gridType = gridTypeForDataset ?? 'mediaCollection'
  const newId = getIdMap[gridType](index)
  const isFocused =
    focusedIndex?.index === index && focusedIndex?.type === gridType
  const isSelected = !!selectedAssetMap[gridType]?.has(newId)
  const history = useHistory()

  const queryFromUrl = useGetQueryParams()

  const singleClickHandler = useSingleClickHandling({
    gridOrder,
    assetCountMap,
    getIdMap,
    selectedAssetMap,
    setSelectedAssetMap,
    focusedIndex,
    setFocusedIndex,
  })

  const {
    isOpen: isOpenChangeThumbnailImageModal,
    onOpen: onOpenChangeThumbnailImageModal,
    onClose: onCloseChangeThumbnailImageModal,
  } = useDisclosure()

  const handleDoubleClick: React.MouseEventHandler<HTMLDivElement> = () => {
    dispatch([
      {
        type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
      },
      {
        type: MediaLibraryActions.RESET_MEDIA_COLLECTIONS_PAGE,
      },
    ])
    history.push(
      `/media-collections/${mediaCollectionId}/${queryFromUrl as string}`,
    )
  }

  const handleEditIconClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    onOpenChangeThumbnailImageModal()
  }

  return (
    <>
      <ContextMenuTrigger
        onTrigger={event => {
          if (!isSelected) {
            setFocusedIndex({ index: index, type: gridType, id: newId })
            dispatch({
              type: MediaLibraryActions.SELECT_MEDIA_COLLECTION,
              payload: {
                mediaCollection,
                event,
              },
            })
          }
        }}
        dispatch={dispatch}
      >
        {contextMenuProps => (
          <VStack
            borderColor={isFocused ? colors.appBlue : colors.appGreyFive}
            borderWidth="1px"
            cursor="pointer"
            onClick={event => {
              event.stopPropagation()
              event.preventDefault()

              singleClickHandler(gridType, index, newId, event)
            }}
            spacing={0}
            onDoubleClick={handleDoubleClick}
            overflow="hidden"
            justifyContent="flex-start"
            alignItems="flex-start"
            borderRadius="8px"
            aria-label={`${index} mediaCollection`}
            data-index={index}
            data-grid-type={gridType}
            className="drag-selectable-element no-right-click-context-menu"
            backgroundColor={isSelected ? "#e6ecf8" : '#fff'}
            {...contextMenuProps}
          >
            <AspectRatio width="100%" ratio={4 / 3.05}>
              <Box
                width="100%"
                position="relative"
                borderBottomWidth="1px"
                padding={'4px'}
                borderBottomColor={colors.appGreyFive}
                _hover={{
                  '& .edit-icon': {
                    opacity: 1,
                    transition: 'opacity 0.2s ease',
                  },
                }}
              >
                {!collectionThumbnail ? (
                  <Icon
                    as={MdCollections}
                    boxSize="100px"
                    color="imagekitGray.500"
                    margin="40px"
                  />
                ) : (
                  <Box borderRadius="4px" overflow="hidden">
                    <Image
                      src={`${collectionThumbnail}?tr=h-300,w-400,cm-pad_resize,fo-center`}
                      alt="Collection Thumbnail"
                      objectFit="contain"
                      draggable={false}
                      onDragStart={e => e.preventDefault()}
                      sx={{ WebkitUserDrag: 'none' }}
                    />
                  </Box>
                )}
                {!isPublicLink && (
                  <Tooltip label="Edit Thumbnail">
                    <Box
                      className="edit-icon"
                      onClick={handleEditIconClick}
                      opacity={0}
                      position="absolute"
                      width="26px"
                      height="26px"
                      bg={colors.appGrey}
                      top="8px"
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      _hover={{
                        bg: colors.appPaleLilac,
                      }}
                      right="8px"
                    >
                      <Icon
                        as={MdEdit}
                        boxSize={4}
                        color={colors.appDarkIndigo}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </AspectRatio>
            <Tooltip label={name} placement="top-start" zIndex={9}>
              <VStack
                spacing={1}
                paddingX={2}
                paddingY={1}
                width="100%"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <Flex
                  width="100%"
                  color={colors.appDarkIndigo}
                  fontWeight="semibold"
                  fontSize="md"
                >
                  <Text
                    className="asset-name"
                    noOfLines={1}
                    width="100%"
                    color={colors.appDarkIndigo}
                  >
                    {name}
                  </Text>
                </Flex>
                <Text
                  color={colors.appSlate}
                  fontSize="sm"
                  isTruncated
                  width="100%"
                >
                  {size === 0
                    ? 'no items'
                    : size === 1
                    ? `${size} item`
                    : `${size} items`}
                </Text>
              </VStack>
            </Tooltip>
          </VStack>
        )}
      </ContextMenuTrigger>
      <Portal>
        {isOpenChangeThumbnailImageModal && (
          <ChangeThumbnailModal
            isOpen={isOpenChangeThumbnailImageModal}
            onClose={onCloseChangeThumbnailImageModal}
            currentSelectedCollection={mediaCollection}
          />
        )}
      </Portal>
    </>
  )
}

export default React.memo(NewCollectionElement)
